import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import { getDefaultRedirectPath, UserRole } from '@/utils/roleUtils'
import { ROUTES } from '@/constants/routes'

/**
 * Component that redirects authenticated users from "/" to their role-specific landing page
 * Since we want role-based default pages, the root "/" should not be accessible
 */
export const RootRedirect: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth)

  // If we have user data, redirect immediately (don't show loading)
  if (user && isAuthenticated) {
    const redirectPath = getDefaultRedirectPath(user.role as UserRole)
    return <Navigate to={redirectPath} replace />
  }

  // If not authenticated and not loading, redirect to login
  if (!isAuthenticated && !isLoading) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  // Only show loading state on initial app load when we truly don't know the auth state
  // and don't have any user data yet
  if (isLoading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Fallback to login if no clear state
  return <Navigate to={ROUTES.LOGIN} replace />
} 